import React, { useState, useEffect } from 'react';
import { templateAPI } from '../../services/templateApi';
import MedicoSelect from '../MedicoSelect';
import { useAuthStore } from '../../store/useStore';
import SelezioneRicerca from '../SelezioneRicerca';
import { SPECIALITA_MEDICHE } from '../../constants/categorie';
import '../../styles/ModuloAnagrafico.css';
import '../../styles/EditorTemplate.css';

/**
 * EditorTemplate
 * Composizione di un modello di consenso informato a blocchi.
 *
 * Formattazione ammessa nei paragrafi: **grassetto** e _corsivo_.
 * Segnaposto sostituiti al momento della firma: {{paziente.nome_cognome}} ecc.
 *
 * Props:
 *  - templateId    se valorizzato, apre in modifica
 *  - prestazioneId se valorizzato, associa il modello a questa prestazione al salvataggio
 *  - onClose()
 *  - onSuccess(template)
 */

const TIPI_BLOCCO = [
    { valore: 'sezione', etichetta: 'Titolo di sezione' },
    { valore: 'paragrafo', etichetta: 'Paragrafo' },
    { valore: 'elenco', etichetta: 'Elenco' },
    { valore: 'consenso', etichetta: 'Casella di consenso' }
];

const SEGNAPOSTO = [
    '{{paziente.nome_cognome}}', '{{paziente.codice_fiscale}}', '{{paziente.data_nascita}}',
    '{{medico.nome_cognome}}', '{{medico.numero_ordine}}', '{{prestazione}}', '{{data}}'
];

const bloccoVuoto = (tipo) => {
    if (tipo === 'elenco') return { tipo, ordinato: false, voci: [''] };
    if (tipo === 'consenso') return { tipo, etichetta: '', descrizione: '', obbligatorio: true };
    return { tipo, testo: '' };
};

/* ============================================================
   Importazione da testo incollato (es. consensi gia' in Word)
   ============================================================ */

const MARCATORE_INIZIALE = /^\s*[([]\s*[)\]]\s*/;
const MARCATORE_OVUNQUE = /[([]\s*[)\]]/g;
const VOCE_ELENCO = /^\s*(?:[-–—•●○◦▪▫‣·*]|\d+[.)])\s+(.*)$/;

// Righe che nel cartaceo servono solo da segnaposto: luogo, data e
// firme le genera il sistema, quindi vanno scartate all'importazione.
const RIGA_SEGNAPOSTO = /^(luogo\s*e\s*data|luogo,?\s*data|data\s*e\s*luogo|data\s*e\s*firma|firma\s+del\s+paziente|firma\s+paziente|firma\s+del\s+medico|firma\s+medico|il\s+paziente|il\s+medico|luogo|data)\b/i;

// "Acconsento ( ) Non acconsento ( )" ovunque dentro una frase
const COPPIA_SI_NO = /(acconsento|autorizzo|consento)\s*[([]\s*[)\]]\s*non\s+(?:acconsento|autorizzo|consento)\s*[([]\s*[)\]]/i;

const RIEMPITIVI = /[.…_]{3,}|\u2122+|\t+/g;

/**
 * Da un paragrafo con marcatori inline ricava le caselle di consenso.
 * Riconosce entrambe le disposizioni usate nei cartacei:
 *   "( ) Opzione"   marcatore prima dell'etichetta
 *   "Opzione ( )"   marcatore dopo l'etichetta
 * Se le etichette risultanti sono troppo lunghe rinuncia e lascia il
 * paragrafo intatto: meglio un "( )" stampato che un testo a pezzi.
 */
const estraiCaselleInline = (testo) => {
    const marcatori = (testo.match(MARCATORE_OVUNQUE) || []).length;
    if (marcatori < 2) return null;

    const pezzi = testo.split(MARCATORE_OVUNQUE).map((p) => p.trim());
    const marcatorePrima = MARCATORE_INIZIALE.test(testo);

    let premessa = '';
    let coda = '';
    let etichette;

    if (marcatorePrima) {
        premessa = pezzi[0];
        etichette = pezzi.slice(1);
    } else {
        etichette = pezzi.slice(0, -1);
        const ultimo = pezzi[pezzi.length - 1];
        // Dopo l'ultimo marcatore puo' esserci un'opzione senza casella
        // oppure la coda del discorso: distinguiamo per lunghezza e
        // punteggiatura finale.
        if (ultimo && ultimo.length <= 80 && !/[.;:]$/.test(ultimo)) {
            etichette.push(ultimo);
        } else {
            coda = ultimo || '';
        }
    }

    // La prima etichetta spesso trascina con se' la frase introduttiva
    if (etichette.length && etichette[0].length > 80) {
        const taglio = Math.max(etichette[0].lastIndexOf(':'), etichette[0].lastIndexOf('. '));
        if (taglio > 0) {
            premessa = [premessa, etichette[0].slice(0, taglio + 1)].filter(Boolean).join(' ').trim();
            etichette[0] = etichette[0].slice(taglio + 1).trim();
        }
    }

    const pulite = etichette.map((e) => e.replace(/^[,;:]\s*/, '').trim()).filter(Boolean);
    if (pulite.length < 2 || pulite.some((e) => e.length > 80)) return null;

    const blocchi = [];
    if (premessa) blocchi.push({ tipo: 'paragrafo', testo: premessa });
    pulite.forEach((etichetta) => {
        blocchi.push({ tipo: 'consenso', etichetta, descrizione: '', obbligatorio: false });
    });
    if (coda) blocchi.push({ tipo: 'paragrafo', testo: coda });
    return blocchi;
};

/**
 * Converte un testo incollato in blocchi.
 * Restituisce { blocchi, firme }.
 */
const analizzaTesto = (testo) => {
    const righe = String(testo).replace(/\r/g, '').split('\n');
    const blocchi = [];
    let paragrafo = [];
    let elenco = null;
    let firmaMedicoTrovata = false;

    const chiudiParagrafo = () => {
        if (!paragrafo.length) return;
        const testoParagrafo = paragrafo.join(' ').trim();
        paragrafo = [];

        // "… Autorizzo ( ) Non autorizzo ( ) …" dentro una frase:
        // diventa casella solo la frase interessata, non tutto il periodo.
        const coppia = testoParagrafo.match(COPPIA_SI_NO);
        if (coppia) {
            const posizione = coppia.index;
            const inizio = testoParagrafo.lastIndexOf('. ', posizione);
            const daQui = inizio >= 0 ? inizio + 2 : 0;
            const dopo = testoParagrafo.indexOf('. ', posizione + coppia[0].length);
            const finoQui = dopo >= 0 ? dopo + 1 : testoParagrafo.length;

            const premessa = testoParagrafo.slice(0, daQui).trim();
            const frase = testoParagrafo.slice(daQui, finoQui).trim();
            const resto = testoParagrafo.slice(finoQui).trim();

            if (premessa) blocchi.push({ tipo: 'paragrafo', testo: premessa });
            blocchi.push({
                tipo: 'consenso',
                // Al posto della coppia di caselle resta il verbo, altrimenti
                // l'etichetta comincerebbe a meta' frase.
                etichetta: frase.replace(COPPIA_SI_NO, coppia[1] + ' ').replace(/\s{2,}/g, ' ').trim(),
                descrizione: '',
                obbligatorio: false
            });
            if (resto) blocchi.push({ tipo: 'paragrafo', testo: resto });
            return;
        }

        const caselle = estraiCaselleInline(testoParagrafo);
        if (caselle) {
            blocchi.push(...caselle);
            return;
        }

        blocchi.push({ tipo: 'paragrafo', testo: testoParagrafo });
    };

    const chiudiElenco = () => {
        if (elenco && elenco.voci.length) blocchi.push(elenco);
        elenco = null;
    };

    righe.forEach((grezza) => {
        const riga = grezza.replace(RIEMPITIVI, ' ').trim();

        if (!riga) {
            chiudiElenco();
            chiudiParagrafo();
            return;
        }

        // Segnaposto di luogo, data e firma: scartati
        if (RIGA_SEGNAPOSTO.test(riga) && riga.length < 90) {
            if (/medico/i.test(riga)) firmaMedicoTrovata = true;
            chiudiElenco();
            chiudiParagrafo();
            return;
        }

        const marcatori = (riga.match(MARCATORE_OVUNQUE) || []).length;

        // "Acconsento ( )   Non acconsento ( )" su riga propria
        if (COPPIA_SI_NO.test(riga) && riga.length < 90) {
            chiudiElenco();
            let etichetta;
            if (paragrafo.length) {
                etichetta = paragrafo.join(' ').trim();
                paragrafo = [];
            } else if (blocchi.length && ['paragrafo', 'sezione'].includes(blocchi[blocchi.length - 1].tipo)) {
                etichetta = blocchi.pop().testo;
            } else if (blocchi.length && blocchi[blocchi.length - 1].tipo === 'elenco') {
                // La casella si riferisce all'elenco appena chiuso:
                // etichetta neutra, da rivedere in fase di composizione.
                etichetta = 'Confermo quanto dichiarato sopra';
            } else {
                etichetta = riga.replace(MARCATORE_OVUNQUE, '').replace(/\s{2,}/g, ' ').trim();
            }
            blocchi.push({ tipo: 'consenso', etichetta, descrizione: '', obbligatorio: false });
            return;
        }

        // "( ) Acconsento al trattamento"
        if (MARCATORE_INIZIALE.test(riga) && marcatori === 1) {
            chiudiElenco();
            chiudiParagrafo();
            const etichetta = riga.replace(MARCATORE_INIZIALE, '').replace(MARCATORE_OVUNQUE, '').trim();
            blocchi.push({ tipo: 'consenso', etichetta, descrizione: '', obbligatorio: false });
            return;
        }

        const voce = riga.match(VOCE_ELENCO);
        if (voce) {
            chiudiParagrafo();
            const ordinato = /^\s*\d/.test(riga);
            if (!elenco) elenco = { tipo: 'elenco', ordinato, voci: [] };
            elenco.voci.push(voce[1].trim());
            return;
        }
        chiudiElenco();

        const senzaPunteggiatura = !/[.:;,]$/.test(riga);
        const tuttoMaiuscolo = riga === riga.toUpperCase() && /[A-ZÀ-Ù]/.test(riga);
        const inPrimaPersona = /^(dichiar|autorizz|acconsent|consent|nego|ho letto|ho compreso|prendo atto|sono stat|il\/la sottoscritt|mi impegno|richiedo|chiedo)/i.test(riga);

        if (paragrafo.length === 0 && !inPrimaPersona && marcatori === 0 &&
            (tuttoMaiuscolo || (riga.length <= 60 && senzaPunteggiatura))) {
            blocchi.push({ tipo: 'sezione', testo: riga });
            return;
        }

        paragrafo.push(riga);
    });

    chiudiElenco();
    chiudiParagrafo();

    // Un titolo andato a capo in Word produce due sezioni consecutive:
    // le riuniamo in una sola.
    const compattati = [];
    blocchi.forEach((b) => {
        const precedente = compattati[compattati.length - 1];
        const entrambiMaiuscoli = precedente
            && precedente.tipo === 'sezione' && b.tipo === 'sezione'
            && precedente.testo === precedente.testo.toUpperCase()
            && b.testo === b.testo.toUpperCase();

        if (entrambiMaiuscoli) {
            precedente.testo = `${precedente.testo} ${b.testo}`.replace(/\s{2,}/g, ' ').trim();
        } else {
            compattati.push(b);
        }
    });

    return {
        blocchi: compattati,
        firme: firmaMedicoTrovata ? 'paziente_medico' : null
    };
};

const EditorTemplate = ({ templateId, prestazioneId, onClose, onSuccess }) => {
    const [titolo, setTitolo] = useState('');
    const [categoria, setCategoria] = useState('');
    const [medicoId, setMedicoId] = useState('');
    /* Chi vede tutti i modelli (vedeTutto del server: amministrazione e
       segreteria) sceglie l'intestatario; gli altri no. */
    const ruolo = useAuthStore((s) => s.user?.ruolo);
    const coordina = ['admin', 'segreteria'].includes(ruolo);
    const [note, setNote] = useState('');
    const [firmeRichieste, setFirmeRichieste] = useState('paziente_medico');
    const [blocchi, setBlocchi] = useState([bloccoVuoto('sezione')]);
    const [caricamento, setCaricamento] = useState(!!templateId);
    const [salvataggio, setSalvataggio] = useState(false);
    const [errore, setErrore] = useState(null);
    const [pannelloImport, setPannelloImport] = useState(false);
    const [testoImport, setTestoImport] = useState('');
    const [sostituisciTutto, setSostituisciTutto] = useState(true);

    /* ---------------- Caricamento ---------------- */

    useEffect(() => {
        let attivo = true;

        const carica = async () => {
            if (!templateId) return;

            try {
                const r = await templateAPI.getById(templateId);
                const t = r.data.data.template;
                if (!attivo) return;
                setTitolo(t.titolo || '');
                setCategoria(t.categoria || '');
                setMedicoId(t.medico_id || '');
                setNote(t.note || '');
                setFirmeRichieste(t.contenuto?.firme === 'paziente' ? 'paziente' : 'paziente_medico');
                setBlocchi(t.contenuto?.blocchi?.length ? t.contenuto.blocchi : [bloccoVuoto('sezione')]);
            } catch (e) {
                if (attivo) setErrore(e?.message || 'Modello non caricato');
            } finally {
                if (attivo) setCaricamento(false);
            }
        };

        carica();
        return () => { attivo = false; };
    }, [templateId]);

    /* ---------------- Manipolazione blocchi ---------------- */

    const aggiornaBlocco = (indice, campo, valore) => {
        setBlocchi((prec) => prec.map((b, i) => (i === indice ? { ...b, [campo]: valore } : b)));
    };

    const cambiaTipo = (indice, tipo) => {
        setBlocchi((prec) => prec.map((b, i) => (i === indice ? bloccoVuoto(tipo) : b)));
    };

    const aggiungiBlocco = (tipo) => {
        setBlocchi((prec) => [...prec, bloccoVuoto(tipo)]);
    };

    const rimuoviBlocco = (indice) => {
        setBlocchi((prec) => prec.filter((_, i) => i !== indice));
    };

    const spostaBlocco = (indice, direzione) => {
        const destinazione = indice + direzione;
        if (destinazione < 0 || destinazione >= blocchi.length) return;
        setBlocchi((prec) => {
            const copia = [...prec];
            [copia[indice], copia[destinazione]] = [copia[destinazione], copia[indice]];
            return copia;
        });
    };

    const aggiornaVoci = (indice, testo) => {
        aggiornaBlocco(indice, 'voci', testo.split('\n'));
    };

    const importa = () => {
        const esito = analizzaTesto(testoImport);
        const nuovi = esito.blocchi;

        if (nuovi.length === 0) {
            setErrore('Il testo incollato non ha prodotto alcun blocco');
            return;
        }

        // Se ci sono solo blocchi vuoti di partenza, li sostituiamo
        const esistenti = sostituisciTutto ? [] : blocchi.filter((b) => {
            /* Stessa regola del salvataggio: i tipi che questo editor non
               conosce restano dove sono. */
            if (!['paragrafo', 'sezione', 'elenco', 'consenso'].includes(b.tipo)) return true;
            if (b.tipo === 'elenco') return (b.voci || []).some((v) => v.trim());
            if (b.tipo === 'consenso') return String(b.etichetta || '').trim() !== '';
            return String(b.testo || '').trim() !== '';
        });

        setBlocchi([...esistenti, ...nuovi]);
        if (esito.firme) setFirmeRichieste(esito.firme);
        setTestoImport('');
        setPannelloImport(false);
        setErrore(null);
    };

    /* ---------------- Salvataggio ---------------- */

    const salva = async () => {
        if (!titolo.trim()) {
            setErrore('Indica il titolo del modello');
            return;
        }

        /* I BLOCCHI CHE QUESTO EDITOR NON SA MODIFICARE NON SI BUTTANO.
           Fino al 16 settembre 2026 l'ultima riga di questo filtro era
           `return String(b.testo || '').trim() !== ''`, e decideva anche
           per i tipi che non conosce. I campi compilabili, le scelte
           obbligate e i riquadri di firma non hanno `testo` ma
           `etichetta` o `titolo`: aprire un consenso caricato da file e
           premere Salva ne cancellava centinaia in silenzio, senza un
           errore, e per giunta faceva avanzare la versione.
           Ora l'elenco dei tipi che si possono scartare e' CHIUSO: quello
           che non e' qui dentro passa cosi' com'e'. */
        const SCARTABILI = ['paragrafo', 'sezione', 'elenco', 'consenso'];

        const puliti = blocchi
            .map((b) => {
                if (b.tipo === 'elenco') {
                    return { ...b, voci: (b.voci || []).map((v) => v.trim()).filter(Boolean) };
                }
                return b;
            })
            .filter((b) => {
                if (!SCARTABILI.includes(b.tipo)) return true;
                if (b.tipo === 'elenco') return b.voci.length > 0;
                if (b.tipo === 'consenso') return String(b.etichetta || '').trim() !== '';
                return String(b.testo || '').trim() !== '';
            });

        if (puliti.length === 0) {
            setErrore('Il modello non contiene alcun blocco compilato');
            return;
        }

        const dati = {
            titolo: titolo.trim(),
            categoria: categoria.trim() || null,
            /* Il medico intestatario lo cambiano solo amministrazione e
               segreteria: dagli altri non parte (Alex, 21/10/2026: un campo
               che il ruolo non puo' cambiare non si manda; il server rifiuta
               col 403 un intestatario diverso, e un modello nuovo lo intesta
               da se' al medico che lo crea). */
            ...(coordina ? { medico_id: medicoId || null } : {}),
            note: note.trim() || null,
            contenuto: { blocchi: puliti, firme: firmeRichieste }
        };

        try {
            setSalvataggio(true);
            setErrore(null);

            const risposta = templateId
                ? await templateAPI.update(templateId, dati)
                : await templateAPI.create(dati);

            const salvato = risposta.data.data.template;

            if (prestazioneId && !templateId) {
                await templateAPI.associa(salvato.id, prestazioneId);
            }

            if (onSuccess) onSuccess(salvato);
            onClose();
        } catch (e) {
            console.error('Errore salvataggio modello:', e);
            setErrore(e?.message || 'Errore nel salvataggio del modello');
        } finally {
            setSalvataggio(false);
        }
    };

    /* I tipi che questo editor sa comporre. Tutto il resto arriva dal
       caricatore dei consensi e si mostra in sola lettura: si puo'
       spostare e togliere, non riscrivere. */
    const MODIFICABILI = ['paragrafo', 'sezione', 'elenco', 'consenso'];
    const ETICHETTA_ALTRI = {
        campo: 'Campo compilabile',
        scelta: 'Scelta obbligata',
        nota: 'Riquadro in evidenza',
        firma: 'Riquadro di firma',
    };

    /* ---------------- Render blocco ---------------- */

    const renderBlocco = (blocco, indice) => (
        <div className="ed-blocco" key={indice}>
            <div className="ed-blocco-barra">
                {MODIFICABILI.includes(blocco.tipo) ? (
                    <select className="ed-tipo" value={blocco.tipo}
                        onChange={(e) => cambiaTipo(indice, e.target.value)}>
                        {TIPI_BLOCCO.map((t) => (
                            <option key={t.valore} value={t.valore}>{t.etichetta}</option>
                        ))}
                    </select>
                ) : (
                    /* Cambiare tipo a un blocco che questo editor non sa
                       comporre vorrebbe dire buttarne il contenuto: un campo
                       compilabile diventerebbe un paragrafo senza testo. */
                    <span className="ed-tipo-fisso">{ETICHETTA_ALTRI[blocco.tipo] || blocco.tipo}</span>
                )}
                <div className="ed-comandi">
                    <button type="button" onClick={() => spostaBlocco(indice, -1)}
                        disabled={indice === 0} title="Sposta su">↑</button>
                    <button type="button" onClick={() => spostaBlocco(indice, 1)}
                        disabled={indice === blocchi.length - 1} title="Sposta giù">↓</button>
                    <button type="button" className="ed-elimina" onClick={() => rimuoviBlocco(indice)}
                        title="Elimina blocco">✕</button>
                </div>
            </div>

            {blocco.tipo === 'sezione' && (
                <input className="input-modulo" value={blocco.testo || ''}
                    placeholder="Es. Descrizione del trattamento"
                    onChange={(e) => aggiornaBlocco(indice, 'testo', e.target.value)} />
            )}

            {blocco.tipo === 'paragrafo' && (
                <textarea className="input-modulo ed-area" rows={4} value={blocco.testo || ''}
                    placeholder="Testo del paragrafo. Usa **grassetto**, _corsivo_ e i segnaposto."
                    onChange={(e) => aggiornaBlocco(indice, 'testo', e.target.value)} />
            )}

            {blocco.tipo === 'elenco' && (
                <>
                    <label className="riga-checkbox ed-opzione">
                        <input type="checkbox" checked={blocco.ordinato === true}
                            onChange={(e) => aggiornaBlocco(indice, 'ordinato', e.target.checked)} />
                        <span>Elenco numerato</span>
                    </label>
                    <textarea className="input-modulo ed-area" rows={4}
                        value={(blocco.voci || []).join('\n')}
                        placeholder={'Una voce per riga\nEs. Ematoma nella sede di iniezione'}
                        onChange={(e) => aggiornaVoci(indice, e.target.value)} />
                </>
            )}

            {!MODIFICABILI.includes(blocco.tipo) && (
                <div className="ed-solo-lettura">
                    <p className="ed-solo-lettura-testo">
                        {blocco.etichetta || blocco.titolo || blocco.testo}
                    </p>
                    {Array.isArray(blocco.opzioni) && blocco.opzioni.length > 0 && (
                        <p className="ed-solo-lettura-opzioni">{blocco.opzioni.join(' · ')}</p>
                    )}
                    <p className="ed-solo-lettura-nota">
                        Arriva dal file del consenso e da qui non si modifica. Resta
                        com&apos;è anche salvando.
                    </p>
                </div>
            )}

            {blocco.tipo === 'consenso' && (
                <>
                    <input className="input-modulo" value={blocco.etichetta || ''}
                        placeholder="Es. Acconsento all'esecuzione del trattamento"
                        onChange={(e) => aggiornaBlocco(indice, 'etichetta', e.target.value)} />
                    <input className="input-modulo ed-secondaria" value={blocco.descrizione || ''}
                        placeholder="Descrizione facoltativa"
                        onChange={(e) => aggiornaBlocco(indice, 'descrizione', e.target.value)} />
                    <label className="riga-checkbox ed-opzione">
                        <input type="checkbox" checked={blocco.obbligatorio !== false}
                            onChange={(e) => aggiornaBlocco(indice, 'obbligatorio', e.target.checked)} />
                        <span>Obbligatorio — senza questo consenso la prestazione non è eseguibile</span>
                    </label>
                </>
            )}
        </div>
    );

    /* ---------------- Render ---------------- */

    if (caricamento) {
        return (
            <div className="modulo-overlay">
                <div className="modulo-finestra ed-finestra">
                    <div className="ed-stato">Caricamento del modello…</div>
                </div>
            </div>
        );
    }

    return (
        <div className="modulo-overlay">
            <div className="modulo-finestra ed-finestra" role="dialog" aria-modal="true">
                <button className="modulo-chiudi ed-chiudi" onClick={onClose} aria-label="Chiudi">✕</button>

                <div className="modulo-body">
                    <h2 className="modulo-titolo">
                        {templateId ? 'Modifica modello di consenso' : 'Nuovo modello di consenso'}
                    </h2>
                    <p className="modulo-nota">
                        Il modello viene compilato con i dati del paziente al momento della firma.
                    </p>

                    <section className="modulo-sezione">
                        <h3>Intestazione</h3>
                        <div className="griglia">
                            <div className="campo col-6">
                                <label>Titolo *</label>
                                <input className="input-modulo" value={titolo}
                                    placeholder="Es. Consenso informato al trattamento con tossina botulinica"
                                    onChange={(e) => setTitolo(e.target.value)} />
                            </div>
                            <div className="campo col-3">
                                <label>Area medica</label>
                                <SelezioneRicerca
                                    valore={categoria}
                                    onChange={setCategoria}
                                    opzioni={SPECIALITA_MEDICHE}
                                    placeholder="Seleziona o cerca…"
                                />
                            </div>
                            <div className="campo col-3">
                                <label>Medico intestatario</label>
                                <MedicoSelect
                                    value={medicoId || null}
                                    onChange={(id) => setMedicoId(id || '')}
                                    placeholder={coordina ? 'Modello di struttura — oppure cerca medico…' : 'Intestato a te'}
                                    disabled={!coordina}
                                />
                                {!coordina && (
                                    <small className="modulo-nota">
                                        Lo cambiano l&apos;amministrazione e la segreteria.
                                    </small>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className="modulo-sezione">
                        <h3>Segnaposto disponibili</h3>
                        <div className="ed-segnaposto">
                            {SEGNAPOSTO.map((s) => (
                                <code key={s}>{s}</code>
                            ))}
                        </div>
                        <p className="modulo-nota">
                            Scrivili nel testo: vengono sostituiti alla firma. Nei paragrafi puoi usare
                            **grassetto** e _corsivo_.
                        </p>
                    </section>

                    <section className="modulo-sezione">
                        <h3>Contenuto</h3>

                        <div className="imp-barra">
                            <button type="button" className="ed-btn-aggiungi imp-apri"
                                onClick={() => setPannelloImport(!pannelloImport)}>
                                {pannelloImport ? 'Chiudi importazione' : 'Importa da testo'}
                            </button>
                        </div>

                        {pannelloImport && (
                            <div className="imp-pannello">
                                <p className="imp-regole">
                                    Incolla qui il consenso che usi su Word. Viene convertito così:
                                </p>
                                <ul className="imp-elenco">
                                    <li><code>( )</code> o <code>[ ]</code> a inizio riga → casella di consenso</li>
                                    <li>più marcatori dentro una frase (es. <em>“Rughe glabellari [ ] Iperidrosi [ ]”</em>)
                                        → una casella per opzione</li>
                                    <li><em>“Acconsento ( ) Non acconsento ( )”</em> → casella di consenso, con
                                        etichetta presa dal testo che la precede</li>
                                    <li><code>-</code> <code>•</code> <code>*</code> <code>1.</code> → voce di elenco</li>
                                    <li>riga in MAIUSCOLO o breve senza punteggiatura → titolo di sezione</li>
                                    <li>righe <em>“Luogo e data”</em>, <em>“Firma del paziente/medico”</em> e i puntini
                                        → scartate: luogo, data e firme le genera il sistema. Se trova la firma del
                                        medico imposta da sola le due firme in calce</li>
                                    <li>tutto il resto → paragrafo</li>
                                </ul>
                                <p className="imp-nota">
                                    Le caselle importate nascono <strong>facoltative</strong>: segna tu quali
                                    sono bloccanti. Dopo l'importazione puoi correggere ogni blocco.
                                </p>
                                <textarea className="input-modulo ed-area" rows={10}
                                    value={testoImport}
                                    placeholder="Incolla qui il testo del consenso…"
                                    onChange={(e) => setTestoImport(e.target.value)} />
                                <div className="imp-azioni">
                                    <label className="riga-checkbox imp-modalita">
                                        <input type="checkbox" checked={sostituisciTutto}
                                            onChange={(e) => setSostituisciTutto(e.target.checked)} />
                                        <span>Sostituisci il contenuto attuale (togli la spunta per aggiungere in coda)</span>
                                    </label>
                                    <button type="button" className="btn-conferma imp-conferma"
                                        onClick={importa} disabled={!testoImport.trim()}>
                                        Converti in blocchi
                                    </button>
                                </div>
                            </div>
                        )}

                        {blocchi.map(renderBlocco)}

                        <div className="ed-aggiungi">
                            {TIPI_BLOCCO.map((t) => (
                                <button key={t.valore} type="button" className="ed-btn-aggiungi"
                                    onClick={() => aggiungiBlocco(t.valore)}>
                                    + {t.etichetta}
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="modulo-sezione">
                        <h3>Firme in calce</h3>
                        <div className="firme-scelta">
                            <label className={`firme-opzione ${firmeRichieste === 'paziente' ? 'attiva' : ''}`}>
                                <input type="radio" checked={firmeRichieste === 'paziente'}
                                    onChange={() => setFirmeRichieste('paziente')} />
                                <div>
                                    <strong>Solo paziente</strong>
                                    <small>Un riquadro di firma allineato a destra</small>
                                </div>
                            </label>
                            <label className={`firme-opzione ${firmeRichieste === 'paziente_medico' ? 'attiva' : ''}`}>
                                <input type="radio" checked={firmeRichieste === 'paziente_medico'}
                                    onChange={() => setFirmeRichieste('paziente_medico')} />
                                <div>
                                    <strong>Paziente e medico</strong>
                                    <small>Due riquadri affiancati, come sui consensi cartacei</small>
                                </div>
                            </label>
                        </div>
                    </section>

                    <section className="modulo-sezione">
                        <h3>Note interne</h3>
                        <textarea className="input-modulo ed-area" rows={2} value={note}
                            placeholder="Non compaiono nel documento firmato"
                            onChange={(e) => setNote(e.target.value)} />
                    </section>

                    {errore && <div className="ed-errore">{errore}</div>}
                </div>

                <footer className="modulo-footer">
                    <button className="btn-annulla" onClick={onClose} disabled={salvataggio}>Annulla</button>
                    <button className="btn-conferma" onClick={salva} disabled={salvataggio}>
                        {salvataggio ? 'Salvataggio…' : 'Salva modello'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default EditorTemplate;
